<template>
  <div
    :data-test-id="`${idPrefix}_create_tinyurl_form`"
    class="py-3 px-3"
    role="dialog"
  >
    <LinksLimitBanner v-if="isLinksBannerVisible" />

    <!-- Single and Bulk Upload Toggler -->
    <div
      v-if="canBulkCreateTinyUrl"
      class="btn-group w-100 mb-4"
    >
      <button
        :data-test-id="`${idPrefix}_create_single_tinyurl`"
        class="py-2 btn rounded-left"
        :class="{ 'btn-teal': !useBulk, 'btn-outline-teal': useBulk }"
        :disabled="isBusy"
        @click="useBulk = false"
      >
        Single
      </button>

      <button
        :data-test-id="`${idPrefix}_create_bulk_tinyurl`"
        class="py-2 btn rounded-right"
        :class="{ 'btn-teal': useBulk, 'btn-outline-teal': !useBulk }"
        :disabled="isBusy"
        @click="useBulk = true"
      >
        Bulk
      </button>
    </div>

    <!-- Form for Single -->
    <template v-if="!useBulk">
      <form
        v-if="!tinyurlCreated"
        autocomplete="off"
        @submit.prevent="doCreateTinyURL"
      >
        <!-- Hidden input to prevent chrome autofill bug -->
        <input
          autocomplete="false"
          name="hidden-non-bulk"
          type="text"
          class="d-none"
        >

        <!-- Team Field -->
        <div
          v-if="inOrganization"
          class="form-group mb-4"
        >
          <label
            class="font-weight-seminormal"
            for="team"
          >
            Select your organization team
          </label>
          <v-select
            id="team"
            ref="team"
            v-model="selectedTeam"
            class="v-select--tinyurl"
            :class="[{ 'is-invalid': urlForm.errors.has('domain') }]"
            label="name"
            :options="teamsOption"
            :reduce="(team) => team.slug"
            :selectable="canUseTheTeam"
            :clearable="false"
          />
        </div>

        <!-- Long URL Field -->
        <div class="form-group mb-4">
          <label
            class="font-weight-seminormal flex-center mb-3"
            for="to-shorten-field"
          >
            &#8204;
            <IconLink />
            <span class="ml-1">Shorten a long URL</span>
          </label>
          <input
            id="to-shorten-field"
            ref="url"
            v-model="urlForm.url"
            autocomplete="to-shorten-field"
            name="to-shorten-field"
            type="text"
            aria-label="Long URL"
            class="form-control toned-placeholder bg-transparent rounded-xl text-green py-4"
            :class="[
              { 'is-invalid': urlForm.errors.has('url') },
              { 'is-invalid': urlForm.errors.has('form') },
            ]"
            :disabled="urlForm.busy"
            placeholder="Enter long link here"
          >
          <ValidationMessage
            :data-test-id="`${idPrefix}_create_tinyurl_validation_form`"
            :message="urlForm.errors.get('form')"
          />
          <ValidationMessage
            :data-test-id="`${idPrefix}_create_tinyurl_validation_url`"
            :message="urlForm.errors.get('url')"
          />
        </div>

        <!-- Domain and Alias Field -->
        <div class="form-group mb-4">
          <label
            class="font-weight-seminormal mb-3"
            for="alias"
          >
            <i class="fa-solid fa-fw fa-wand-magic-sparkles" />
            <span class="ml-1">Customize your link</span>
          </label>
          <div class="form-row mb-0">
            <div class="col-md-7 mb-2 mb-md-0">
              <v-select
                id="domain"
                ref="domain"
                v-model="urlForm.domain"
                class="v-select--tinyurl"
                :class="[{ 'is-invalid': urlForm.errors.has('domain') }]"
                label="domain"
                :options="allowedAvailableDomains"
                :clearable="false"
                :selectable="canChooseDomain"
                :reduce="(data) => data.domain"
              >
                <template #selected-option="selected">
                  {{ selected.domain }}
                </template>
                <template #option="option">
                  <template v-if="!canChooseDomain(option)">
                    <div
                      class="flex-center-between"
                      @click.stop="customizeDomain(option.domain)"
                    >
                      <span class="text-nowrap">{{ option.domain }}</span>
                      <span class="text-blue text-sm ml-2">
                        <i
                          v-tooltip="{
                            content: `This domain is not available to guest users.`,
                            trigger: isOnMobile ? 'click' : 'focus hover',
                          }"
                          class="fa fa-lock"
                          tabindex="0"
                        />
                      </span>
                    </div>
                  </template>
                  <template v-else>
                    {{ option.domain }}
                  </template>
                </template>
                <template #list-footer>
                  <li class="mt-1 p-2 text-reset border-top">
                    <a
                      class="btn btn-block btn-blue"
                      @click.stop="addDomain"
                    >
                      <div class="d-flex justify-content-between text-no-wrap">
                        <span class="text-nowrap">Add Domain</span>
                        <span
                          v-if="!canUseBrandedDomains"
                          class="ml-2"
                        >
                          <i class="fa fa-lock" />
                        </span>
                      </div>
                    </a>
                  </li>
                </template>
              </v-select>
              <ValidationMessage
                :id="`${idPrefix}_create_tinyurl_validation_domain`"
                :message="urlForm.errors.get('domain')"
              />
            </div>
            <div class="col-md-5 mb-0">
              <div
                v-tooltip="{
                  content: customAliasTooltipText(),
                  trigger: isOnMobile ? 'click' : 'focus hover',
                }"
                tabindex="0"
                @click.stop="customizeAlias"
              >
                <input
                  id="alias"
                  v-model="urlForm.alias"
                  aria-label="Alias"
                  name="alias"
                  type="text"
                  class="form-control toned-placeholder bg-transparent rounded-xl text-green py-4"
                  :class="[{ 'is-invalid': urlForm.errors.has('alias') }]"
                  :placeholder="$t('Enter alias')"
                  :disabled="urlForm.busy || !customAliasAllowed"
                  @keydown.enter="doCreateTinyURL"
                >
              </div>

              <i
                v-if="!customAliasAllowed"
                class="fa fa-lock text-blue position-absolute"
                style="top: 17px; right: 20px"
              />

              <ValidationMessage
                :data-test-id="`${idPrefix}_create_tinyurl_validation_alias`"
                :message="urlForm.errors.get('alias')"
              />
            </div>
          </div>
        </div>

        <!-- Description Field -->
        <div
          v-if="inOrganization"
          class="form-group mb-4"
        >
          <label
            class="font-weight-seminormal"
            for="description"
          >
            Write Description (optional)
          </label>
          <textarea
            id="description"
            v-model="urlForm.description"
            class="form-control bg-transparent"
            :class="[{ 'is-invalid': urlForm.errors.has('description') }]"
            rows="5"
          />
          <ValidationMessage :message="urlForm.errors.get('description')" />
        </div>

        <!-- Tags Field -->
        <div
          v-if="canManageTags"
          class="form-group mb-4"
        >
          <label
            class="font-weight-seminormal"
            for="currentTag"
          >
            {{ $t("add_tags") }} (optional)
          </label>
          <div
            class="d-flex align-items-start flex-wrap"
            style="gap: 10px"
          >
            <div
              v-for="t in urlForm.tags"
              :key="t"
              :data-test-id="`${idPrefix}_create_tinyurl_field_tag`"
              class="bg-gray-400 cursor-pointer flex-center py-2 px-3 rounded-xl text-break text-lg"
              @click="manageTag('remove', t)"
            >
              <span
                :data-test-id="`${idPrefix}_create_tinyurl_field_tag_text`"
              >{{ t }}</span>
              <i
                :data-test-id="`${idPrefix}_create_tinyurl_field_tag_btn`"
                class="fa-solid fa-trash text-gray-500 ml-2"
              />
            </div>

            <div
              class="flex-center"
              style="max-width: 175px"
            >
              <input
                id="currentTag"
                v-model="currentTag"
                list="tagHistory"
                name="currentTag"
                type="text"
                class="border border-gray-400 rounded form-controls toned-placeholder bg-transparent rounded-xl text-green text-lg py-2 px-3 w-100"
                :class="{ 'is-invalid': urlForm.errors.has('tags') }"
                :placeholder="$t('tag')"
                @input="changeTagToLowerCase"
                @keypress.prevent.enter="manageTag('add', currentTag)"
              >
              <datalist id="tagHistory">
                <option
                  v-for="t in tagHistory"
                  :key="t"
                  :value="t"
                />
              </datalist>
              <button
                :data-test-id="`${idPrefix}_create_tinyurl_add_tag_btn`"
                class="btn btn-gray-100 border-0 font-weight-semibold"
                type="button"
                @click="manageTag('add', currentTag)"
              >
                <i class="fa-solid fa-plus fa-lg text-blue" />
              </button>
            </div>
          </div>
          <ValidationMessage :message="urlForm.errors.get('tags')" />
          <template v-for="(t, k) in urlForm.tags">
            <ValidationMessage
              v-if="`tags.${k}` in urlForm.errors.all()"
              :key="t"
              class="mb-0"
              :message="urlForm.errors.all()[`tags.${k}`].join('<br>')"
            />
          </template>
        </div>

        <!-- Advanced Options -->
        <div
          v-if="hasAdvancedOptions"
          class="mt-3"
        >
          <div class="advance-options">
            <div
              class="advance-options-content"
              @click="toggleExtendedMode"
            >
              <i
                v-if="extendedMode"
                class="fas fa-chevron-up"
              />
              <i
                v-else
                class="fas fa-chevron-down"
              />
              <span class="mx-2">Advanced Options</span>
              <i
                v-if="extendedMode"
                class="fas fa-chevron-up"
              />
              <i
                v-else
                class="fas fa-chevron-down"
              />
            </div>
          </div>
          <template v-if="extendedMode">
            <!-- UTM -->
            <div class="flex-center mb-3">
              <FormSwitch
                v-model="withUtm"
                accessibility-label="UTM Manager Toggler"
                input-id="utm"
              />
              <label
                class="text-dark mb-0 ml-2 d-flex"
                for="utm"
              >
                Add UTM
                <i
                  v-tooltip="{
                    content: `Use UTM tags to get more details about your traffic sources when using analytics tools outside of TinyURL`,
                    trigger: isOnMobile ? 'click' : 'focus hover',
                  }"
                  class="fa fa-info-circle text-blue text-sm ml-1 _utm-info"
                  tabindex="0"
                />
              </label>
              <a
                v-if="withUtm"
                href="javascript:void(0)"
                class="cursor-pointer text-blue ml-3 _edit-utm"
                @click.prevent="showModal('utm-builder')"
              >
                <i class="fa-solid fa-pencil text-sm" /> Edit
              </a>
            </div>

            <!-- Link Expiry -->
            <div class="form-group mb-3">
              <div
                class="flex-center text-nowrap"
                :class="{ 'mb-2': useCustomExpiration }"
              >
                <FormSwitch
                  v-model="useCustomExpiration"
                  accessibility-label="Expiration Date Toggler"
                  input-id="expiry"
                />
                <label
                  class="text-dark mb-0 ml-2"
                  for="expiry"
                >
                  Set Link Expiry
                </label>
                <small class="ml-1 text-muted">(Local time)</small>
              </div>

              <div
                v-if="useCustomExpiration"
                class="flex-grow-1 mr-2"
              >
                <DatePicker
                  v-model="expiresAt"
                  class="w-100"
                  type="datetime"
                  format="MMM DD, YYYY hh:mm A"
                  :placeholder="expirationDatePlaceholderText"
                  :input-class="[
                    'mx-input py-4 rounded-10px toned-placeholder',
                    { 'is-invalid': urlForm.errors.has('expires_at') },
                  ]"
                  :disabled="urlForm.busy"
                  :disabled-date="disabledExpirationDateRange"
                  :disabled-time="disabledExpirationTimeRange"
                  @open="datepickerOpened = true"
                  @close="datepickerOpened = false"
                >
                  <template #icon-calendar>
                    <i class="fa-solid fa-calendar text-blue" />
                  </template>
                </DatePicker>
                <ValidationMessage
                  :message="urlForm.errors.get('expires_at')"
                />
              </div>
            </div>
          </template>
        </div>

        <!-- Terms Agreement -->
        <label
          v-if="requireCCPA"
          class="custom-checkbox-2 custom-checkbox-2--xs"
          for="ccpa"
        >
          <input
            id="ccpa"
            v-model="ccpa"
            type="checkbox"
            name="ccpa"
          >
          <span class="text-muted text-xs">
            I agree to the
            <router-link
              class="text-blue"
              :to="{ name: 'terms' }"
            >Terms of Service</router-link>,
            <router-link
              class="text-blue"
              :to="{ name: 'privacy' }"
            >Privacy Policy</router-link>, and
            <router-link
              class="text-blue"
              :to="{ name: 'cookies' }"
            >Use of Cookies</router-link>.
          </span>
          <span class="checkmark" />
        </label>

        <TurnstileWidget
          v-if="isTurnstileAvailable"
          ref="turnstile"
          v-model="urlForm.captcha_token"
          :sitekey="getSiteKey"
          @verify="onTurnstileVerified"
          @error="onTurnstileError"
          @expired="onTurnstileExpired"
        />

        <!-- Shorten URL Button -->
        <div class="form-group mb-0">
          <button
            data-test-id="home_shortener_btn_create"
            type="submit"
            class="btn btn-block font-weight-seminormal btn-xl btn-green"
            :disabled="isCreateButtonDisabled"
          >
            <i
              v-if="urlForm.busy"
              class="fa fa-spinner fa-spin"
            />
            <template v-else>
              {{ $t("shorten_link") }}
            </template>
          </button>
        </div>
      </form>

      <template v-else>
        <div class="form-group mb-3">
          <label for="to-shorten-field">
            <img
              class="mr-2"
              src="@Images/home/url.svg"
              alt="Link icon"
            > Your Long URL
          </label>
          <input
            id="to-shorten-field"
            class="form-control form-control-lg text-break text-green"
            readonly
            :value="createdTinyurl.url"
          >
        </div>
        <div class="form-group mb-3">
          <label>
            <img
              class="mr-2"
              src="@Images/home/magic-wand.svg"
              alt="Magic wand icon"
            > TinyURL
          </label>
          <div class="mt-n2">
            <input
              v-for="mainAlias in mainAliases"
              :key="mainAlias.id"
              :data-test-id="`${idPrefix}_create_tinyurl_form_created_input`"
              class="form-control form-control-lg mt-2 text-break text-green"
              readonly
              :value="decodeURI(mainAlias.tiny_url)"
            >
            <div
              v-if="isAliasAffiliated(mainAliases)"
              class="small"
            >
              {{ $t("tinyurl_affiliated_notice") }}
              <a
                href="affiliate-link"
                @click.stop.prevent="showAffiliationPop"
              >
                <u>{{ $t("learn_more") }}</u>
              </a>
            </div>
          </div>
        </div>
        <div class="d-flex flex-wrap mb-3">
          <VisitTinyurl
            prefix="form"
            :url="mainAliasUri"
          />
          <QRDropdown
            prefix="form"
            :url="mainAliasUri"
          />
          <ShareDropdown
            prefix="form"
            :url="mainAliasUri"
          />
          <CopyTinyurl
            prefix="form"
            :url="mainAliasUri"
          />
        </div>
        <div class="d-flex flex-wrap-reverse m-n2">
          <div
            v-if="!canUseAnalytics"
            class="p-2 flex-fill"
          >
            <router-link
              data-test-id="myurls_link_form"
              class="btn btn-xl btn-outline-green px-3 btn-block text-nowrap"
              :to="{ name: 'my-urls' }"
            >
              My URLs
            </router-link>
          </div>
          <div class="flex-grow-1 p-2">
            <button
              :data-test-id="`${idPrefix}_create_tinyurl_form_shorten_another_btn`"
              class="btn btn-xl btn-green btn-block text-nowrap"
              @click="resetTinyurl"
            >
              Shorten another
            </button>
          </div>
        </div>
      </template>
    </template>

    <!-- Form for Bulk -->
    <template v-else>
      <form
        v-if="!bulkStatus"
        autocomplete="off"
        @submit.prevent="bulkCreate"
      >
        <!-- Team Field -->
        <div
          v-if="inOrganization"
          class="form-group mb-4"
        >
          <label
            class="font-weight-seminormal"
            for="team"
          >
            Select your organization team
          </label>
          <select
            id="team"
            v-model="selectedTeam"
            class="form-control"
          >
            <option :value="null">
              Personal
            </option>
            <option
              v-for="t in organizationTeams"
              :key="t.slug"
              :value="t.slug"
              :disabled="!canUseTheTeam(t)"
            >
              <span>{{ t.name }}</span>
              <span>{{ !canUseTheTeam(t) ? "(Viewer Access Only)" : "" }}</span>
            </option>
          </select>
        </div>

        <!-- CSV File -->
        <div class="form-group mb-4">
          <div class="flex-center-between">
            <label
              class="flex-center-center font-weight-seminormal"
              for="csv-file"
            >
              <div style="flex: 0 0 19px">
                <IconLink />
              </div>
              <span class="ml-2">
                Select a file to make a batch of TinyURLs
              </span>
            </label>
            <div class="mb-2 pl-2">
              <v-popover
                trigger="hover"
                class="_bulk-guide"
              >
                <i class="fa-solid fa-lg fa-circle-question" />
                <div
                  slot="popover"
                  class="popover-component _bulk-guide-popover"
                >
                  <p class="font-weight-semibold">
                    Please follow the guide accordingly.
                  </p>
                  <img
                    class="popover-bulk-guide mb-1"
                    :src="bulkGuide"
                    alt="Bulk upload guide figure"
                  >
                  <p class="flex-center mb-0">
                    <i
                      class="fa-solid fa-circle fa-fw mt-1 mr-1 d-none"
                      style="font-size: 6px"
                    />
                    <span class="font-weight-semibold">Accepted file type</span>: csv, xls, xlsx, ods
                  </p>
                  <p class="flex-center mb-0">
                    <i
                      class="fa-solid fa-circle fa-fw mt-1 mr-1 d-none"
                      style="font-size: 6px"
                    />
                    <span class="font-weight-semibold">Maximum file size</span>:
                    10MB
                  </p>
                </div>
              </v-popover>
            </div>
          </div>
          <div
            class="border p-3 rounded"
            :class="[{ 'is-invalid': bulkUrlForm.errors.has('file') }]"
          >
            <input
              id="csv-file"
              type="file"
              class="form-control-file text-green"
              accept=".csv, text/csv, .xlsx, .ods"
              :disabled="isBusy"
              @change="processFile"
            >
          </div>
          <ValidationMessage
            :data-test-id="`${idPrefix}_create_tinyurl_validation_file`"
            :message="bulkUrlForm.errors.get('file')"
          />
          <div
            class="custom-control custom-checkbox mt-2"
            style="margin-left: -0.7rem"
          >
            <input
              id="bulk-has-header"
              v-model="bulkUrlForm.withHeader"
              type="checkbox"
              class="custom-control-input"
              :disabled="isBusy"
            >
            <label
              for="bulk-has-header"
              class="custom-control-label"
            >
              File contains header row?
            </label>
          </div>
        </div>

        <!-- Default Domain -->
        <div class="form-group mb-4">
          <label
            class="font-weight-seminormal"
            for="domain"
          >
            <i class="fa-solid fa-fw fa-wand-magic-sparkles" />
            <span class="ml-1">Default domain</span>
          </label>
          <div class="form-row">
            <div class="col-12 col-md-7 pr-md-0 pt-1">
              <v-select
                id="domain"
                ref="domain"
                v-model="bulkUrlForm.domain"
                class="v-select--tinyurl"
                :class="[{ 'is-invalid': urlForm.errors.has('domain') }]"
                label="domain"
                :options="allowedAvailableDomains"
                :clearable="false"
                :selectable="canChooseDomain"
                :reduce="(data) => data.domain"
                :disabled="isBusy"
              >
                <template #selected-option="selected">
                  {{ selected.domain }}
                </template>
                <template #option="option">
                  <template v-if="!canChooseDomain(option)">
                    <div
                      class="flex-center-between"
                      @click.stop="customizeDomain(option.domain)"
                    >
                      <span class="text-nowrap">{{ option.domain }}</span>
                      <i class="fa fa-lock text-blue text-sm ml-2" />
                    </div>
                  </template>
                  <template v-else>
                    {{ option.domain }}
                  </template>
                </template>
                <template #list-footer>
                  <li class="mt-1 p-2 text-reset border-top">
                    <a
                      class="btn btn-block btn-blue"
                      @click.stop="addDomain"
                    >
                      <div class="d-flex justify-content-between text-no-wrap">
                        <span class="text-nowrap">Add Domain</span>
                        <span
                          v-if="!canUseBrandedDomains"
                          class="ml-2"
                        >
                          <i class="fa fa-lock" />
                        </span>
                      </div>
                    </a>
                  </li>
                </template>
              </v-select>
            </div>
          </div>
        </div>

        <!-- Shorten URL Button -->
        <div class="form-group mb-4">
          <button
            data-test-id="home_shortener_btn_create"
            class="btn btn-block btn-xl btn-green"
            :disabled="isUploadButtonDisabled"
            @click.prevent="bulkCreate"
          >
            <template v-if="bulkUrlForm.busy">
              <i class="fa fa-spinner fa-spin" />
              Uploading...
            </template>
            <template v-else>
              {{ $t("shorten_link") }}
            </template>
          </button>
        </div>
      </form>

      <template v-else>
        <div class="mb-3">
          <label :data-test-id="`${idPrefix}_success_bulk_tinyurl_batch`">
            <i class="far fa-clock" /> Batch scheduled
          </label>
        </div>
        <div class="mb-2">
          <label :data-test-id="`${idPrefix}_success_bulk_tinyurl_items_added`">Total items added</label>
          <span
            :data-test-id="`${idPrefix}_success_bulk_tinyurl_items_added_count`"
            class="mr-3"
          >{{ bulkStatus?.total }}</span>
        </div>
        <div class="p-2">
          <button
            data-test-id="home_shortener_btn_start_new_batch"
            class="btn btn-xl btn-green btn-block text-nowrap"
            @click="resetBulk"
          >
            Start another batch
          </button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import DatePicker from "vue2-datepicker";
import vSelect from "vue-select";

import { debounce } from "lodash-es";

import MixinBreakpoint from "@Mixins/bootstrap/breakpoint";
import MixinOrganization from "@Mixins/organizations";
import MixinOrganizationPermissions from "@Mixins/organizations/permissions";
import MixinUserTeam from "@Mixins/user-team";

import FormSwitch from "@Forms/FormSwitch.vue";
import LinksLimitBanner from "@Components/banners/LinksLimit.vue";
import VisitTinyurl from "@Components/sharing/VisitTinyurl.vue";
import QRDropdown from "@Components/sharing/QRDropdown.vue";
import ShareDropdown from "@Components/sharing/ShareDropdown.vue";
import CopyTinyurl from "@Components/sharing/CopyTinyurl.vue";
import IconLink from "@Components/icons/IconLink.vue";
import UTMBuilder from "@Modals/UTMBuilder.vue";
import ValidationMessage from "@Components/forms/ValidationMessage.vue";
import TurnstileWidget from "@Components/turnstile/TurnstileWidget.vue";

import useCaptcha from "@Composables/useCaptcha";
import useLimit from "@Composables/useLimit";
import useNotify from "@Composables/useNotify";

import urlStringParamsToJson from "@Helpers/functions/urlStringParamsToJson";
import { TINYURL_EXPIRATION_DATE_PLACEHOLDER } from "@Helpers/constant";
import { sleep } from "@Helpers/support";

import LIMITS_TYPE from "@Constants/limits-type";
import { OVERAGE_STATUS } from "@Constants/overage-status";

export default {
  components: {
    vSelect,
    DatePicker,
    FormSwitch,
    LinksLimitBanner,
    VisitTinyurl,
    QRDropdown,
    ShareDropdown,
    CopyTinyurl,
    IconLink,
    ValidationMessage,
    TurnstileWidget,
  },

  mixins: [
    MixinBreakpoint,
    MixinOrganization,
    MixinOrganizationPermissions,
    MixinUserTeam,
  ],

  setup() {
    const { getSiteKey, isAntiRobotWhitelisted, getAntiRobotTestToken } = useCaptcha();
    const linksLimit = useLimit(LIMITS_TYPE.LINKS_PER_MONTH);
    const { notifyError, notifySuccess } = useNotify();

    return {
      linksLimit,
      notifyError,
      notifySuccess,
      getSiteKey,
      isAntiRobotWhitelisted,
      getAntiRobotTestToken,
    };
  },

  data: () => ({
    // Single URL Options
    datepickerOpened: false,
    extendedMode: true,
    createdTinyurl: {},
    requireCCPA: false,
    useCustomExpiration: false,
    expirationDatePlaceholderText: TINYURL_EXPIRATION_DATE_PLACEHOLDER,
    isTurnstileVerified: false,
    currentTag: "",
    withUtm: null,
    urlForm: new SparkForm({
      url: "",
      domain: "",
      alias: "",
      description: "",
      team: null,
      tags: [],
      captcha_token: null,

      // if null, alias will never expires
      // if has valid date, alias will use the date passed
      // if has an invalid date, alias will use the default date from their plan
      expires_at: null,
    }),

    // Bulk Upload URL Options
    useBulk: false,
    bulkStatus: null,
    isFileSizeValid: true,
    bulkUrlForm: new SparkForm({
      file: undefined,
      domain: "",
      team: null,
      withHeader: true,
    }),

    selectedTeam: null,
  }),

  computed: {
    ...mapGetters([
      "isAccountUsageEnabled",
      "isOnMobile",
      "isUserLoggedIn",
      "userVerified",
      "isUserSubscribed",
      "isUserVerifiedOrSubscribed",
      "user",
      "userOptions",
      "termsAccepted",
      "canUseAnalytics",
      "availableDomains",
      "canUseCustomAlias",
      "canUseBrandedDomains",
      "canBulkCreateTinyUrl",
      "canManageExpiration",
      "canUseTags",
      "ccpaRequired",
      "ccpaAccepted",
      "flashMessages",
      "team",
      "organization",
      "organizationDomains",
      "organizationTeams",
      "tinyurls",
      "urls",
      "commonDomains",
      "getUserCurrentSubscription",
    ]),

    isLinksBannerVisible() {
      if (
        !this.isUserSubscribed ||
        this.getUserCurrentSubscription?.isLegacy
      ) {
        return this.linksLimit.isHardLimitReached.value;
      }

      return (
        this.linksLimit.isHardLimitReached.value &&
        !this.linksLimit.hasStatus(OVERAGE_STATUS.ALL_ENABLE)
      );
    },

    isCreateButtonDisabled() {
      return (
        this.isBusy ||
        !this.canCreateTinyURL ||
        !this.isTurnstileVerified ||
        this.isLinksBannerVisible
      );
    },

    isUploadButtonDisabled() {
      return (
        this.isBusy ||
        !this.canCreateTinyURL ||
        !this.isFileSizeValid ||
        this.isLinksBannerVisible
      );
    },

    isTurnstileAvailable() {
      return !this.isUserLoggedIn && !this.isAntiRobotWhitelisted;
    },

    bulkGuide() {
      return `/images/home/bulk-guide-${this.inOrganization ? "organization" : "personal"}.png`;
    },

    idPrefix() {
      return this.inOrganization
        ? "organization"
        : this.isUserLoggedIn
          ? "dashboard"
          : "homepage";
    },

    allowedOrganizationDomains() {
      return this.organizationDomains.filter((o) => {
        // if for_all_teams or for "Personal" use
        if (o.for_all_teams || !this.selectedTeam) return true;
        return o.teams.some((t) => t.slug === this.selectedTeam);
      });
    },

    allowedAvailableDomains() {
      let finalDomains = [];

      if (this.inOrganization) {
        const defaultD = _.cloneDeep(this.commonDomains.available).map((d) => {
          d.type = "common";
          return d;
        });

        finalDomains = [...this.allowedOrganizationDomains, ...defaultD];
      } else {
        finalDomains = this.availableDomains;
      }

      return finalDomains
        .filter(
          (data) => !data.isDisabled && !data.isDeleted && !data.isBlocked,
        )
        .sort((a, b) =>
          a.domain > b.domain ? 1 : b.domain > a.domain ? -1 : 0,
        );
    },

    isBusy() {
      return this.urlForm.busy || this.bulkUrlForm.busy;
    },

    closable() {
      return !(this.datepickerOpened || this.isBusy);
    },

    mainAliases() {
      return _.map(this.createdTinyurl.aliases, (aliasGroup) =>
        this.getMainAlias(aliasGroup),
      );
    },

    tinyurlCreated() {
      return !_.isEmpty(this.createdTinyurl);
    },

    customAliasAllowed() {
      if (!this.isUserLoggedIn) {
        const domainData = this.findDomainData(this.urlForm.domain);

        if (!domainData) return false;
        return domainData.guestCanUseCustomAlias;
      }

      return this.canUseCustomAlias;
    },

    canCreateTinyURL() {
      return !this.requireCCPA || this.ccpa;
    },

    ccpa: {
      get: function () {
        return this.ccpaAccepted;
      },
      set: function (accepted) {
        this.acceptCCPA(accepted);
      },
    },

    hasAdvancedOptions() {
      if (this.inOrganization) {
        return this.canOrganizationManageExpirationDate(this.selectedTeam);
      } else {
        return this.canManageExpiration;
      }
    },

    createRequestUrl() {
      let url = this.$apiUrl("url/create");

      if (this.inOrganization) {
        // change request url
        if (this.selectedTeam) {
          url = this.$apiUrl("organization/{o}/team/{t}/url/create", {
            o: this.organization.slug,
            t: this.selectedTeam,
          });
        } else {
          url = this.$apiUrl("organization/{o}/personal/url/create", {
            o: this.organization.slug,
          });
        }

        // remove tags if not permitted
        if (!this.canOrganizationManageTags(this.selectedTeam)) {
          this.$delete(this.urlForm, "tags");
        }
      }

      return url;
    },

    bulkRequestUrl() {
      let url = this.$apiUrl("bulk/csv");

      if (this.inOrganization) {
        // change request url
        if (this.selectedTeam) {
          url = this.$apiUrl("organization/{o}/team/{t}/bulk/csv", {
            o: this.organization.slug,
            t: this.selectedTeam,
          });
        } else {
          url = this.$apiUrl("organization/{o}/personal/bulk/csv", {
            o: this.organization.slug,
          });
        }
      }

      return url;
    },

    teamsOption() {
      return [{ name: "Personal", slug: null }, ...this.organizationTeams];
    },

    canManageTags() {
      if (this.inOrganization) {
        return this.canOrganizationManageTags(this.selectedTeam);
      } else {
        return this.canUseTags;
      }
    },

    expiresAt: {
      get: function () {
        return this.urlForm.expires_at
          ? moment.utc(this.urlForm.expires_at).toDate()
          : null;
      },
      set: function (value) {
        this.urlForm.expires_at = value
          ? moment(value).utc().toISOString()
          : null;
      },
    },

    tagHistory() {
      const getTags = (urls) => urls.aliases.map((a) => a.tags);

      const tags = [
        ...this.tinyurls?.data.map(getTags),
        ...this.urls?.map(getTags),
      ];

      return _.union(_.flattenDeep(tags));
    },

    mainAliasUri() {
      return decodeURI(this.mainAliases[0].tiny_url);
    },
  },

  watch: {
    user() {
      this.requireCCPA = !this.isUserLoggedIn && this.ccpaRequired;
      this.setDomain(this.urlForm.domain, this.commonDomains.current);
    },

    team() {
      this.setDomain(this.urlForm.domain, this.commonDomains.current);
    },

    termsAccepted(newValue) {
      if (newValue) this.focusUrlInput();
    },

    closable() {
      this.$emit("closable", this.closable);
    },

    flashMessages() {
      this.legacyCreate();
    },

    withUtm(isOn) {
      if (isOn) {
        if (!this.urlForm.url) {
          this.urlForm.setErrors({
            ...this.urlForm.errors.all(),
            url: ["The URL field is required."],
          });

          this.$nextTick(() => {
            this.withUtm = false;
          });
        } else {
          this.showModal("utm-builder");
        }
      } else {
        this.removeUtm();
      }
    },

    isTurnstileAvailable(isAvailable) {
      this.isTurnstileVerified = !isAvailable;
    },
  },

  created() {
    if (this.inOrganization) {
      // fetch the organization domains
      this.fetchOrgDomains({
        options: { force: true },
      });

      // set the team automatically
      if (this.organizationTeam) {
        this.selectedTeam = this.canUseTheTeam(this.organizationTeam)
          ? this.organizationTeam.slug
          : null;
      } else {
        this.selectedTeam = null;
      }
    }
  },

  mounted() {
    this.isTurnstileVerified = !this.isTurnstileAvailable;
    this.requireCCPA = !this.isUserLoggedIn && this.ccpaRequired;
    this.setDomain(this.commonDomains.current);

    if (this.termsAccepted) {
      this.focusUrlInput();
      this.legacyCreate();
    }
  },

  methods: {
    ...mapActions([
      "fetchOrgDomains",
      "fetchTinyurl",
      "acceptTerms",
      "acceptCCPA",
      "refreshState",
    ]),

    notifyTurnstileError() {
      const message = `An error has occurred during turnstile validation, please try again. If the error persists, please contact us.`;

      this.urlForm.captcha_token = null;
      this.isTurnstileVerified = false;

      this.notifyError({ text: message });
    },

    onTurnstileError: debounce(
      function () {
        this.notifyTurnstileError();
      },
      60000,
      { leading: true },
    ),

    onTurnstileVerified() {
      this.isTurnstileVerified = true;
    },

    onTurnstileExpired() {
      this.urlForm.captcha_token = null;
      this.isTurnstileVerified = false;
    },

    doCreateTinyURL() {
      let onCreateSuccess = () => {};
      let onCreateError = () => {};

      if (this.isAntiRobotWhitelisted) {
        this.urlForm.captcha_token = this.getAntiRobotTestToken();
      }

      if (this.isTurnstileAvailable) {
        onCreateSuccess = () => {
          this.isTurnstileVerified = false;
        };

        onCreateError = () => {
          this.isTurnstileVerified = false;
          this.$refs.turnstile.reset();
        };
      }

      this.createTinyurl(onCreateSuccess, onCreateError);
    },

    changeTagToLowerCase(e) {
      this.currentTag = e.target.value.toLowerCase();
    },

    canUseTheTeam(team) {
      if (this.isUserAuthorized("teams")) {
        return true;
      } else {
        if (team.membership?.role != "viewer") return true;
      }

      return false;
    },

    disabledExpirationDateRange(date) {
      // get the current date
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      // add 5 years to the current date
      const futureDate = new Date(
        currentDate.getFullYear() + 5,
        currentDate.getMonth(),
        currentDate.getDate(),
      );

      return date < currentDate || date > futureDate;
    },

    disabledExpirationTimeRange(time) {
      const date = new Date(time);
      date.setSeconds(0);

      const start = new Date();
      start.setMinutes(start.getMinutes() + 5); // do not allow same minute
      start.setSeconds(59);

      return start && date < start;
    },

    findDomainData(domain) {
      return this.allowedAvailableDomains.find(
        (data) => data.domain === domain,
      );
    },

    canChooseDomain(domainData) {
      if (!this.isUserLoggedIn) {
        return domainData.allowForGuests;
      } else {
        if (
          domainData.requireEmailVerification &&
          !this.isUserVerifiedOrSubscribed
        ) {
          return false;
        }

        if (
          !this.inOrganization &&
          domainData.subscribedOnly &&
          !this.isUserSubscribed
        ) {
          return false;
        }
      }

      return true;
    },

    register() {
      this.$refs.domain.closeSearchOptions();
      this.$router.push({ name: "register" });
    },

    verify() {
      this.$refs.domain.closeSearchOptions();
      this.$router.push({ name: "email_verify" });
    },

    customizeAlias() {
      if (!this.customAliasAllowed) {
        if (!this.isUserLoggedIn) {
          this.register();
        } else if (!this.isUserVerifiedOrSubscribed) {
          this.verify();
        }
      }
    },

    customAliasTooltipText() {
      if (this.customAliasAllowed) return false;

      return this.isUserVerifiedOrSubscribed
        ? this.$t("custom_alias_not_allowed")
        : this.$t("email_verification");
    },

    customizeDomain(domain) {
      // this.notifyError({
      //   text: "This domain is not available to guest users.",
      // });

      if (!this.isUserLoggedIn) this.register();
      else if (!this.userVerified) this.verify();
      // if (!this.isUserVerifiedOrSubscribed) this.verify();
    },

    addDomain() {
      Bus.$emit("closePopover2");

      if (!this.isUserLoggedIn) {
        this.notifyError({
          text: this.$t("log_in_or_register", {
            login_url: "login",
            register_url: "register",
          }),
        });
        this.register();
      } else if (!this.isUserVerifiedOrSubscribed) {
        this.notifyError({ text: this.$t("email_verification_check") });
        this.verify();
      } else if (!this.canUseBrandedDomains) {
        this.notifyError({ text: this.$t("billing.not_subscribed") });
        this.$router.push({ name: "settings.subscription" });
      } else {
        this.$router.push({ name: "settings.brandedDomains" });
      }
    },

    getMainAlias(aliasGroup) {
      return aliasGroup.find((alias) => {
        return alias.is_main;
      });
    },

    isEmpty(v) {
      return _.isEmpty(v);
    },

    toggleExtendedMode() {
      this.extendedMode = !this.extendedMode;
    },

    createThenMyUrls() {
      this.createTinyurl(() => {
        if (!this.canUseAnalytics) {
          this.$router.push({ name: "my-urls" });
        }
      });
    },

    /**
     * Creates a tiny url.
     *
     * @param {Function | undefined} onSuccess An optional callback that occurs when the creation is successfully completed.
     * @param {Function | undefined} onError   An optional callback that occurs when the creation fails.
     */
    createTinyurl(onSuccess = () => {}, onError = () => {}) {
      this.urlForm.team = this.selectedTeam;
      this.setExpirationDate();

      Spark.post(this.createRequestUrl, this.urlForm)
        .then(({ data }) => {
          const tinyurl = _.first(data);
          tinyurl.aliases = data.map((urlData) => {
            return urlData.aliases.map((alias) => {
              alias.copied = false;
              return alias;
            });
          });

          const alias = `${tinyurl.aliases[0][0].domain}/${tinyurl.aliases[0][0].alias}`;

          if (this.inOrganization) {
            this.fetchTinyurl({
              organization: this.organization.slug,
              ...(this.selectedTeam && { team: this.selectedTeam }),
              alias,
              callback: (data) => {
                this.createdTinyurl = tinyurl;

                const tinyurls = this.$store.getters.tinyurls.data;
                const doesTinyurlExists = tinyurls.some((t) => t.url == data.url);

                if (!doesTinyurlExists) {
                  if (this.$route.name == "org.dashboard") {
                    this.$store.commit("PUSH_TINYURL", data);
                  } else if (this.$route.name == "org.me") {
                    if (!this.selectedTeam) {
                      this.$store.commit("PUSH_TINYURL", data);
                    }
                  } else if (this.$route.name == "org.team") {
                    if (this.organizationTeam.slug == this.selectedTeam) {
                      this.$store.commit("PUSH_TINYURL", data);
                    }
                  }
                }

                this.$router
                  .push({
                    query: {
                      ...(this.selectedTeam && { team: this.selectedTeam }),
                      alias,
                    },
                  })
                  .catch(() => {});
              },
            });
          } else {
            this.createdTinyurl = tinyurl;
            Bus.$emit("tinyurl-created");

            if (this.canUseAnalytics) {
              this.$router.push({
                name: "homepage",
                query: {
                  alias,
                  active: true,
                },
              });
            }

            onSuccess();
          }
        })
        .catch(() => {
          onError();

          if (this.urlForm.errors.has("url")) {
            this.focusUrlInput();
          }
        })
        .finally(() => {
          // track event in meta pixel
          if (window.fbq) {
            window.fbq("track", "shortern-url-button-clicked");
          }

          this.acceptTerms();
        });
    },

    resetTinyurl() {
      this.extendedMode = true;
      this.useCustomExpiration = false;
      this.withUtm = null;
      this.$set(this.urlForm, "url", "");
      this.$set(this.urlForm, "alias", "");
      this.$set(this.urlForm, "description", "");
      this.$set(this.urlForm, "tags", []);
      this.$set(this.urlForm, "expires_at", null);
      this.createdTinyurl = {};
      this.focusUrlInput();
    },

    resetBulk() {
      this.extendedMode = true;
      this.$set(this.bulkUrlForm, "file", undefined);
      this.bulkStatus = null;
    },

    setExpirationDate() {
      let date = null;

      if (this.useCustomExpiration && this.expiresAt) {
        date = moment(this.expiresAt).utc().format("YYYY-MM-DDTHH:mm");
      }

      this.$set(this.urlForm, "expires_at", date);
    },

    async focusUrlInput() {
      await sleep(300);
      this.$refs.url.focus();
    },

    setDomain(...domains) {
      let found = false;

      domains.forEach((domain) => {
        if (!found && domain) {
          const domainData = this.findDomainData(domain);

          if (domainData !== undefined && this.canChooseDomain(domainData)) {
            this.urlForm.domain = domainData.domain;
            this.bulkUrlForm.domain = domainData.domain;
            found = true;
          }
        }
      });

      if (!found) {
        this.notifyError({ text: this.$t("domain_not_found") });
        this.urlForm.domain = window.location.hostname;
        this.bulkUrlForm.domain = window.location.hostname;
      }
    },

    isAliasAffiliated(aliases) {
      return _.get(_.first(aliases), "affiliates");
    },

    showAffiliationPop() {
      this.$router.push({ name: "affiliateOptionPop" });
    },

    processFile(event) {
      this.bulkUrlForm.file = event.target.files[0];

      const megabyte = 1024 * 1024;
      const maxSize = megabyte * 10;

      if (this.bulkUrlForm.file && this.bulkUrlForm.file.size > maxSize) {
        this.isFileSizeValid = false;
        this.bulkUrlForm.setErrors({
          file: [this.$t("shortener.bulk_upload_file_size_exceeds", { size: "10MB" })],
        });
      } else {
        this.isFileSizeValid = true;
        const errors = { ...this.bulkUrlForm.errors.all() };
        delete errors.file;
        this.bulkUrlForm.setErrors(errors);
      }
    },

    bulkCreate() {
      this.bulkUrlForm.team = this.selectedTeam;

      const formData = new FormData();
      formData.append("file", this.bulkUrlForm.file);
      formData.append("domain", this.bulkUrlForm.domain);
      formData.append("withHeader", this.bulkUrlForm.withHeader);

      this.bulkUrlForm.startProcessing();
      axios
        .post(this.bulkRequestUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(({ data }) => {
          this.bulkUrlForm.finishProcessing();
          this.bulkStatus = data;
          this.notifySuccess({ text: this.$t("shortener.bulk_upload_submit_feedback") });
        })
        .catch((errors) => {
          this.bulkUrlForm.setErrors(errors.response.data.errors);
        });
    },

    legacyCreate() {
      if (this.flashMessages && this.flashMessages.legacyCreate) {
        this.urlForm.url = this.flashMessages.legacyCreate.url || "";
        this.urlForm.alias = this.flashMessages.legacyCreate.alias || "";

        if (this.urlForm.url.length > 0 && this.canCreateTinyURL) {
          this.createThenMyUrls();
          this.$nextTick(() => {
            this.refreshState();
          });
        }
      }
    },

    manageTag(method, tag) {
      tag = tag.replaceAll(" ", "");

      const errors = { ...this.urlForm.errors.all() };
      delete errors.tags;
      this.urlForm.setErrors(errors);

      if (method == "add") {
        if (this.urlForm.tags.includes(tag)) {
          this.urlForm.errors.set({
            tags: ["The tag has already been taken."],
          });
        } else if (tag.length == 1) {
          this.urlForm.errors.set({
            tags: ["The tag must be at least 2 characters."],
          });
        } else if (tag.length > 45) {
          this.urlForm.errors.set({
            tags: ["The tag must not be greater than 45 characters."],
          });
        }

        if (!_.isEmpty(tag) && !this.urlForm.errors.has("tags")) {
          this.currentTag = "";
          this.$set(this.urlForm, "tags", [...this.urlForm.tags, tag]);
        }
      }

      if (method == "remove") {
        this.urlForm.tags = this.urlForm.tags.filter((t) => t != tag);
      }
    },

    showModal(type) {
      if (type == "utm-builder") {
        if (!this.urlForm.url) {
          this.urlForm.setErrors({ url: ["The URL field is required."] });
          return;
        }

        Bus.$emit("showModal", UTMBuilder, {
          method: "add",
          destination: this.urlForm.url,
          callback: (rebuildUrl) => {
            this.urlForm.url = rebuildUrl;
          },
        });
      }
    },

    removeUtm() {
      const params = urlStringParamsToJson(this.urlForm.url);
      delete params["utm_source"];
      delete params["utm_medium"];
      delete params["utm_campaign"];
      delete params["utm_term"];
      delete params["utm_content"];

      const url = this.urlForm.url.split("?")[0];
      const str = new URLSearchParams(params).toString();

      this.urlForm.url = `${url}${str ? `?${str}` : ``}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.popover-bulk-guide {
  @media (max-width: 767px) {
    width: 500px;
  }

  @media (max-width: 575px) {
    width: 315px;
  }

  @media (max-width: 320px) {
    width: 275px;
  }
}

input {
  &.form-control {
    &[readonly] {
      background-color: transparent;
    }

    &:focus {
      border: 1px solid #e5e5e5;
      box-shadow: none;
    }
  }

  &#currentTag::-webkit-calendar-picker-indicator {
    display: none !important;
  }
}
</style>
